import { SvgNamespace } from '../enum';

const STYLE_PROPS = [
  'fill',
  'stroke',
  'color',
  'opacity',
  'fill-opacity',
  'stroke-opacity',
  'stroke-width',
  'stroke-dasharray',
  'stroke-dashoffset',
  'stroke-linecap',
  'stroke-linejoin',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'letter-spacing',
  'text-anchor',
  'dominant-baseline',
  'paint-order'
];

const inlineSvgStyles = (source: SVGElement, target: SVGElement) => {
  const sourceNodes = source.querySelectorAll<SVGElement>('*');
  const targetNodes = target.querySelectorAll<SVGElement>('*');

  sourceNodes.forEach((node, index) => {
    const targetNode = targetNodes[index];
    if (!targetNode) {
      return;
    }

    const computed = window.getComputedStyle(node);
    const style = STYLE_PROPS.map((prop) => `${prop}:${computed.getPropertyValue(prop)}`).join(';');
    if (style) {
      const existing = targetNode.getAttribute('style');
      targetNode.setAttribute('style', existing ? `${existing};${style}` : style);
    }
  });
};

const normalizeSvgIds = (svg: SVGSVGElement) => {
  const nodesWithId = Array.from(svg.querySelectorAll<SVGElement>('[id]'));
  const idMap = new Map<string, string>();
  const usedIds = new Set<string>();

  const toSafeId = (id: string) => id.replace(/[^a-zA-Z0-9_-]/g, '_');
  const ensureUniqueId = (base: string) => {
    if (!usedIds.has(base)) {
      usedIds.add(base);
      return base;
    }
    let index = 1;
    while (usedIds.has(`${base}_${index}`)) {
      index += 1;
    }
    const uniqueId = `${base}_${index}`;
    usedIds.add(uniqueId);
    return uniqueId;
  };

  nodesWithId.forEach((node) => {
    const id = node.getAttribute('id');
    if (!id) {
      return;
    }

    const normalizedBase = toSafeId(id);
    const normalized = ensureUniqueId(normalizedBase);
    if (normalized === id) {
      usedIds.add(id);
    } else {
      idMap.set(id, normalized);
      node.setAttribute('id', normalized);
    }
  });

  if (idMap.size === 0) {
    return;
  }

  const attributesToUpdate = [
    'fill',
    'stroke',
    'filter',
    'clip-path',
    'mask',
    'marker-start',
    'marker-mid',
    'marker-end',
    'href',
    'xlink:href'
  ];
  const urlRegex = /url\(#([^)]+)\)/g;
  const hashRegex = /^#(.+)$/;

  svg.querySelectorAll<SVGElement>('*').forEach((node) => {
    attributesToUpdate.forEach((attr) => {
      const value = node.getAttribute(attr);
      if (!value) {
        return;
      }

      let updated = value.replace(urlRegex, (match: string, id: string) => {
        const normalized = idMap.get(id);
        return normalized ? `url(#${normalized})` : match;
      });

      const hashMatch = updated.match(hashRegex);
      if (hashMatch) {
        const normalized = idMap.get(hashMatch[1]);
        if (normalized) {
          updated = `#${normalized}`;
        }
      }

      if (updated !== value) {
        node.setAttribute(attr, updated);
      }
    });

    const style = node.getAttribute('style');
    if (style) {
      const updatedStyle = style.replace(urlRegex, (match: string, id: string) => {
        const normalized = idMap.get(id);
        return normalized ? `url(#${normalized})` : match;
      });
      if (updatedStyle !== style) {
        node.setAttribute('style', updatedStyle);
      }
    }
  });
};

const LEGEND_PADDING = 16;

const buildLegendGroup = (legend: HTMLElement, legendRect: DOMRect) => {
  const group = document.createElementNS(SvgNamespace.Svg, 'g');

  const items = Array.from(legend.querySelectorAll<HTMLElement>('li'));
  items.forEach((item) => {
    const key = item.querySelector<SVGSVGElement>('svg[data-testid="ChartLegendKey"]');
    const label = item.querySelector<HTMLElement>('span');
    if (!key || !label) {
      return;
    }

    const itemRect = item.getBoundingClientRect();
    const keyRect = key.getBoundingClientRect();
    const labelRect = label.getBoundingClientRect();

    if (!itemRect.width || !labelRect.width) {
      return;
    }

    const keyClone = key.cloneNode(true) as SVGSVGElement;
    keyClone.removeAttribute('class');
    keyClone.setAttribute('x', `${keyRect.left - legendRect.left}`);
    keyClone.setAttribute('y', `${keyRect.top - legendRect.top}`);
    keyClone.setAttribute('width', `${Math.max(1, Math.ceil(keyRect.width))}`);
    keyClone.setAttribute('height', `${Math.max(1, Math.ceil(keyRect.height))}`);

    const text = document.createElementNS(SvgNamespace.Svg, 'text');
    const computed = window.getComputedStyle(label);
    text.setAttribute('x', `${labelRect.left - legendRect.left}`);
    text.setAttribute('y', `${labelRect.top - legendRect.top + labelRect.height / 2}`);
    text.setAttribute('dominant-baseline', 'middle');
    text.setAttribute('font-family', computed.fontFamily);
    text.setAttribute('font-size', computed.fontSize);
    text.setAttribute('font-weight', computed.fontWeight);
    text.setAttribute('fill', computed.color);
    text.textContent = label.textContent ?? '';

    group.appendChild(keyClone);
    group.appendChild(text);
  });

  return group;
};

const sanitizeBarRects = (svg: SVGSVGElement) => {
  const barRects = svg.querySelectorAll<SVGElement>('[data-testid="BarRect"]');
  barRects.forEach((bar) => {
    bar.removeAttribute('style');
    if (!bar.getAttribute('opacity')) {
      bar.setAttribute('opacity', '1');
    }
  });
};

export const getSvgExportPayload = (container: HTMLElement | null) => {
  if (!container) {
    return null;
  }

  const svgCandidate =
    (container instanceof SVGSVGElement ? container : null) ||
    container.querySelector('[data-testid="LineChartSvg"]') ||
    container.querySelector('[data-testid="BarChartSvg"]');

  if (!svgCandidate) {
    return null;
  }

  if (!(svgCandidate instanceof SVGSVGElement)) {
    return null;
  }

  const svg = svgCandidate;
  const rect = svg.getBoundingClientRect();
  const viewBox = svg.viewBox?.baseVal;
  const baseWidth = Math.ceil(rect.width || viewBox?.width || Number(svg.getAttribute('width')) || 0) || 800;
  const baseHeight = Math.ceil(rect.height || viewBox?.height || Number(svg.getAttribute('height')) || 0) || 450;
  let width = baseWidth;
  let height = baseHeight;

  const svgClone = svg.cloneNode(true) as SVGSVGElement;
  svgClone.setAttribute('width', `${width}`);
  svgClone.setAttribute('height', `${height}`);

  if (!svgClone.getAttribute('xmlns')) {
    svgClone.setAttribute('xmlns', SvgNamespace.Svg);
  }

  if (!svgClone.getAttribute('xmlns:xlink')) {
    svgClone.setAttribute('xmlns:xlink', SvgNamespace.XLink);
  }

  inlineSvgStyles(svg, svgClone);
  normalizeSvgIds(svgClone);

  const isBarChart = svg.getAttribute('data-testid') === 'BarChartSvg';
  if (isBarChart) {
    sanitizeBarRects(svgClone);
  }

  const isLineChart = svg.getAttribute('data-testid') === 'LineChartSvg';
  if (isLineChart) {
    const legend = container.querySelector<HTMLElement>('[data-testid="ChartLegend"]');
    if (legend) {
      const legendRect = legend.getBoundingClientRect();
      if (legendRect.height > 0) {
        const legendGroup = buildLegendGroup(legend, legendRect);
        const legendOffsetY = baseHeight + LEGEND_PADDING;
        legendGroup.setAttribute('transform', `translate(0 ${legendOffsetY})`);
        svgClone.appendChild(legendGroup);

        width = Math.max(width, Math.ceil(legendRect.width));
        height = Math.ceil(legendOffsetY + legendRect.height);
        svgClone.setAttribute('width', `${width}`);
        svgClone.setAttribute('height', `${height}`);
        if (viewBox) {
          svgClone.setAttribute('viewBox', `0 0 ${width} ${height}`);
        }
      }
    }
  }

  const serialized = new XMLSerializer().serializeToString(svgClone);
  const svgString = serialized && serialized.trim().length > 0 ? serialized : svgClone.outerHTML;

  return { svgString, width, height };
};
